import { useEffect, useCallback, useRef, useState } from 'react';
import { fmt$ } from '../utils/helpers.js';

/* ── RADAR ── */
export function useRadar(ref, stats){
  useEffect(()=>{
    const cv=ref.current;if(!cv)return;
    const dpr=window.devicePixelRatio||1, DIM=200;
cv.width=DIM*dpr;cv.height=DIM*dpr;cv.style.width=DIM+'px';cv.style.height=DIM+'px';
const ctx=cv.getContext('2d');ctx.scale(dpr,dpr);
const cx=DIM/2,cy=DIM/2,r=DIM/2-36;
    ctx.clearRect(0,0,DIM,DIM);
    const axes=['Win %','Consistence','R:R Ratio','Profit Factor','Streak'];
    const vals=[
      Math.min(1,stats.wr/100),
      Math.min(1,1-Math.abs(stats.al)/Math.max(Math.abs(stats.aw),1)),
      Math.min(1,parseFloat(stats.avgrr)/4),
      Math.min(1,(typeof stats.pf==='string'?0:parseFloat(stats.pf))/4),
      Math.min(1,stats.weekStreak/5),
    ];
    const n=axes.length;
    for(let ring=1;ring<=4;ring++){
      ctx.beginPath();
      for(let a=0;a<n;a++){
        const ang=-Math.PI/2+a/n*Math.PI*2,pr=r*ring/4;
        a===0?ctx.moveTo(cx+Math.cos(ang)*pr,cy+Math.sin(ang)*pr):ctx.lineTo(cx+Math.cos(ang)*pr,cy+Math.sin(ang)*pr);
      }
      ctx.closePath();ctx.strokeStyle='rgba(201,168,76,.08)';ctx.lineWidth=1;ctx.stroke();
    }
    for(let a=0;a<n;a++){
      const ang=-Math.PI/2+a/n*Math.PI*2;
      ctx.beginPath();ctx.moveTo(cx,cy);ctx.lineTo(cx+Math.cos(ang)*r,cy+Math.sin(ang)*r);
      ctx.strokeStyle='rgba(201,168,76,.12)';ctx.lineWidth=1;ctx.stroke();
      ctx.fillStyle='rgba(200,192,168,.55)';ctx.font='9px Inter,sans-serif';
      ctx.textAlign='center';ctx.textBaseline='middle';
      ctx.fillText(axes[a],cx+Math.cos(ang)*(r+13),cy+Math.sin(ang)*(r+13));
    }
    ctx.beginPath();
    vals.forEach((v,a)=>{
      const ang=-Math.PI/2+a/n*Math.PI*2,vr=r*v;
      a===0?ctx.moveTo(cx+Math.cos(ang)*vr,cy+Math.sin(ang)*vr):ctx.lineTo(cx+Math.cos(ang)*vr,cy+Math.sin(ang)*vr);
    });
    ctx.closePath();
    const g=ctx.createRadialGradient(cx,cy,0,cx,cy,r);
    g.addColorStop(0,'rgba(201,168,76,.35)');g.addColorStop(1,'rgba(201,168,76,.06)');
    ctx.fillStyle=g;ctx.fill();
    ctx.strokeStyle='rgba(201,168,76,.7)';ctx.lineWidth=1.5;ctx.stroke();
    vals.forEach((v,a)=>{
      const ang=-Math.PI/2+a/n*Math.PI*2,vr=r*v;
      ctx.beginPath();ctx.arc(cx+Math.cos(ang)*vr,cy+Math.sin(ang)*vr,3,0,Math.PI*2);
      ctx.fillStyle='#C9A84C';ctx.fill();
    });
  },[ref,stats]);
}

/* ── PIE / DONUT ── */
export function usePieChart(ref,trades){
  useEffect(()=>{
    const cv=ref.current;if(!cv)return;
    const dpr=window.devicePixelRatio||1,DIM=120;
    cv.width=DIM*dpr;cv.height=DIM*dpr;cv.style.width=DIM+'px';cv.style.height=DIM+'px';
    const ctx=cv.getContext('2d');ctx.scale(dpr,dpr);
    const w=trades.filter(t=>t.result>0).length,l=trades.filter(t=>t.result<0).length;
    const tot=w+l,cx=DIM/2,cy=DIM/2,R=DIM/2-8,thick=14;
    ctx.clearRect(0,0,DIM,DIM);
    ctx.beginPath();ctx.arc(cx,cy,R,0,Math.PI*2);ctx.strokeStyle='rgba(255,255,255,.06)';ctx.lineWidth=thick;ctx.stroke();
    if(!tot){
      ctx.fillStyle='rgba(112,104,96,.6)';ctx.font='bold 9px Inter,sans-serif';ctx.textAlign='center';ctx.textBaseline='middle';ctx.fillText('NO DATA',cx,cy);
    }else{
      const segs=[
        {v:w,c1:'rgba(61,190,115,.9)',c2:'rgba(61,190,115,.5)',c:'#3DBE73'},
        {v:l,c1:'rgba(217,79,79,.9)',c2:'rgba(217,79,79,.5)',c:'#D94F4F'},
      ].filter(s=>s.v>0);
      let startA=-Math.PI/2;
      segs.forEach(seg=>{
        const sweep=(seg.v/tot)*Math.PI*2-0.03;if(sweep<=0)return;
        const endA=startA+sweep;
        const grd=ctx.createLinearGradient(cx+Math.cos(startA)*R,cy+Math.sin(startA)*R,cx+Math.cos(endA)*R,cy+Math.sin(endA)*R);
        grd.addColorStop(0,seg.c1);grd.addColorStop(1,seg.c2);
        ctx.beginPath();ctx.arc(cx,cy,R,startA,endA);ctx.strokeStyle=grd;ctx.lineWidth=thick;ctx.lineCap='round';ctx.stroke();
        startA=endA+0.03;
      });
      ctx.fillStyle='#C9A84C';ctx.font="bold 22px Inter,sans-serif";ctx.textAlign='center';ctx.textBaseline='middle';
      ctx.fillText(Math.round(w/tot*100)+'%',cx,cy-6);
      ctx.fillStyle='rgba(200,192,168,.75)';ctx.font='500 9px Inter,sans-serif';ctx.fillText('WIN RATE',cx,cy+9);
    }
  },[ref,trades]);
}

/* ── EQUITY CURVE (small, sidebar) ── */
export function useEquityCurve(ref,trades){
  useEffect(()=>{
    const cv=ref.current;if(!cv||!trades.length)return;
    const draw=()=>{
      const dpr=window.devicePixelRatio||1;
      const W=cv.parentElement?.offsetWidth||240;
      cv.width=W*dpr;cv.height=62*dpr;
      cv.style.width=W+'px';cv.style.height='62px';
      const ctx=cv.getContext('2d');ctx.scale(dpr,dpr);
      const H=62;ctx.clearRect(0,0,W,H);
      const pts=[...trades].sort((a,b)=>a.date.localeCompare(b.date));
      let run=0;const vals=pts.map(t=>{run+=t.result;return run;});
      if(vals.length<2)return;
      const mn=Math.min(...vals,0),mx=Math.max(...vals,.01),range=mx-mn||1;
      const xs=i=>(i/(vals.length-1))*(W-10)+5;
      const ys=v=>H-8-((v-mn)/range)*(H-16);
      const net=vals[vals.length-1];
      const lc=net>=0?'#3DBE73':'#D94F4F';
      const fb=net>=0?'61,190,115':'217,79,79';
      const g=ctx.createLinearGradient(0,0,0,H);
      g.addColorStop(0,`rgba(${fb},.3)`);g.addColorStop(1,'rgba(0,0,0,0)');
      ctx.beginPath();ctx.moveTo(xs(0),ys(vals[0]));
      vals.forEach((v,i)=>{if(i>0)ctx.lineTo(xs(i),ys(v));});
      ctx.lineTo(xs(vals.length-1),H);ctx.lineTo(xs(0),H);ctx.closePath();ctx.fillStyle=g;ctx.fill();
      ctx.beginPath();ctx.moveTo(xs(0),ys(vals[0]));
      vals.forEach((v,i)=>{if(i>0)ctx.lineTo(xs(i),ys(v));});
      ctx.strokeStyle=lc;ctx.lineWidth=1.5;ctx.stroke();
    };
    draw();
    window.addEventListener('resize',draw);
    return()=>window.removeEventListener('resize',draw);
  },[ref,trades]);
}

/* ── PERFORMANCE OVERVIEW CHART (interactive) ── */
export function PerfOverviewChart({trades, filter='ALL'}){
  const cvRef=useRef(null);
  const [hoverIdx,setHoverIdx]=useState(null);

  const now=new Date();
  const filtered=trades.filter(t=>{
    if(filter==='ALL')return true;
    const d=new Date(t.date);
    const diff=(now-d)/(1000*60*60*24);
    return filter==='3M'?diff<=90:diff<=30;
  });

  const sorted=[...filtered].sort((a,b)=>a.date.localeCompare(b.date));
  let run=0;
  const points=sorted.map(t=>{run+=t.result;return{val:run,result:t.result,pair:t.pair,date:t.date};});

  const draw=useCallback(()=>{
    const cv=cvRef.current;if(!cv||points.length<1)return;
    const dpr=window.devicePixelRatio||1;
    const W=cv.parentElement.offsetWidth||340,H=120;
    cv.width=W*dpr;cv.height=H*dpr;
    cv.style.width=W+'px';cv.style.height=H+'px';
    const ctx=cv.getContext('2d');ctx.scale(dpr,dpr);
    ctx.clearRect(0,0,W,H);
    if(points.length<2)return;
    const vals=points.map(p=>p.val);
    const mn=Math.min(...vals,0),mx=Math.max(...vals,.01),range=mx-mn||1;
    const pad={l:40,r:8,t:8,b:22};
    const xs=i=>pad.l+(i/(points.length-1))*(W-pad.l-pad.r);
    const ys=v=>H-pad.b-((v-mn)/range)*(H-pad.t-pad.b);

    // Y axis labels
    ctx.fillStyle='rgba(200,192,168,.55)';ctx.font='9px Inter,sans-serif';ctx.textAlign='right';
    [mn,mn+range*0.25,mn+range*0.5,mn+range*0.75,mx].forEach(v=>{
      ctx.fillText(fmt$(v,false),pad.l-4,ys(v)+3);
      ctx.beginPath();ctx.moveTo(pad.l,ys(v));ctx.lineTo(W-pad.r,ys(v));
      ctx.strokeStyle='rgba(255,255,255,.04)';ctx.lineWidth=1;ctx.stroke();
    });

    // Zero line
    if(mn<0&&mx>0){
      ctx.beginPath();ctx.moveTo(pad.l,ys(0));ctx.lineTo(W-pad.r,ys(0));
      ctx.strokeStyle='rgba(255,255,255,.1)';ctx.lineWidth=1;ctx.setLineDash([3,4]);ctx.stroke();ctx.setLineDash([]);
    }

    // Fill
    const isPos=vals[vals.length-1]>=0;
    const grad=ctx.createLinearGradient(0,0,0,H);
    grad.addColorStop(0,isPos?'rgba(61,190,115,.2)':'rgba(217,79,79,.2)');
    grad.addColorStop(1,'rgba(0,0,0,0)');
    ctx.beginPath();ctx.moveTo(xs(0),ys(points[0].val));
    points.forEach((p,i)=>{if(i>0)ctx.lineTo(xs(i),ys(p.val));});
    ctx.lineTo(xs(points.length-1),H-pad.b);ctx.lineTo(xs(0),H-pad.b);
    ctx.closePath();ctx.fillStyle=grad;ctx.fill();

    // Line
    ctx.beginPath();ctx.moveTo(xs(0),ys(points[0].val));
    points.forEach((p,i)=>{if(i>0)ctx.lineTo(xs(i),ys(p.val));});
    ctx.strokeStyle=isPos?'#3DBE73':'#D94F4F';ctx.lineWidth=1.5;ctx.lineJoin='round';ctx.stroke();

    // Dots + X labels
    ctx.fillStyle='rgba(200,192,168,.55)';ctx.font='8px Inter,sans-serif';ctx.textAlign='center';
    points.forEach((p,i)=>{
      const isH=hoverIdx===i;
      if(i===0||i===points.length-1||isH){
        ctx.fillText(p.date.slice(5).replace('-','/'),xs(i),H-4);
      }
      ctx.beginPath();ctx.arc(xs(i),ys(p.val),isH?5:2.5,0,Math.PI*2);
      ctx.fillStyle=p.result>0?'#3DBE73':'#D94F4F';ctx.globalAlpha=isH?1:0.7;ctx.fill();ctx.globalAlpha=1;
      if(isH){
        ctx.beginPath();ctx.arc(xs(i),ys(p.val),8,0,Math.PI*2);
        ctx.strokeStyle=p.result>0?'rgba(61,190,115,.3)':'rgba(217,79,79,.3)';ctx.lineWidth=1;ctx.stroke();
      }
    });
  },[points,hoverIdx]);

  useEffect(()=>{
    draw();
    window.addEventListener('resize',draw);
    return()=>window.removeEventListener('resize',draw);
  },[draw]);

  const hit=e=>{
    const cv=cvRef.current;if(!cv||points.length<2)return;
    const rect=cv.getBoundingClientRect();
    const mx=(e.touches?e.touches[0].clientX:e.clientX)-rect.left-40;
    const W=rect.width-48;
    setHoverIdx(Math.max(0,Math.min(points.length-1,Math.round(mx/W*(points.length-1)))));
  };

  return(
    <div style={{position:'relative'}}>
      <div style={{width:'100%',overflow:'hidden'}}>
        <canvas ref={cvRef}
          onMouseMove={hit} onMouseLeave={()=>setHoverIdx(null)}
          onTouchMove={hit} onTouchEnd={()=>setHoverIdx(null)}
          style={{display:'block',cursor:'crosshair'}}/>
      </div>
      {hoverIdx!==null&&points[hoverIdx]&&(
        <div style={{position:'absolute',top:4,right:6,background:'rgba(5,5,5,.95)',border:'1px solid rgba(201,168,76,.2)',padding:'5px 10px',fontSize:10,pointerEvents:'none'}}>
          <div style={{color:'var(--gold)',fontFamily:"'Inter',sans-serif",fontSize:8,letterSpacing:'.1em',marginBottom:2}}>{points[hoverIdx].pair} · {points[hoverIdx].date}</div>
          <div style={{color:points[hoverIdx].result>=0?'var(--green)':'var(--red)',fontWeight:600}}>{fmt$(points[hoverIdx].result)}</div>
          <div style={{color:'rgba(200,192,168,.45)',fontSize:9}}>Equity {fmt$(points[hoverIdx].val)}</div>
        </div>
      )}
    </div>
  );
}

/* ── PIPS BAR CHART ── */
export function PipsBarChart({trades}){
  const last10=[...trades].sort((a,b)=>b.date.localeCompare(a.date)).slice(0,10).reverse();
  if(!last10.length)return <div style={{height:90,display:'flex',alignItems:'center',justifyContent:'center',color:'rgba(200,192,168,.2)',fontSize:11}}>No data</div>;
  const maxAbs=Math.max(...last10.map(t=>Math.abs(Number(t.pips)||0)),1);
  const H=70;
  return(
    <div style={{padding:'0 2px'}}>
      <div style={{display:'flex',alignItems:'flex-end',gap:2,height:H,position:'relative'}}>
        <div style={{position:'absolute',left:0,right:0,top:'50%',height:1,background:'rgba(255,255,255,.08)'}}/>
        {last10.map((t,i)=>{
          const p=Number(t.pips)||0,pos=p>=0;
          const bh=Math.max(3,Math.abs(p)/maxAbs*(H/2-4));
          return(
            <div key={i} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',height:'100%',position:'relative',justifyContent:'center'}}>
              <div style={{position:'absolute',top:pos?`calc(50% - ${bh}px - 13px)`:'auto',bottom:pos?'auto':`calc(50% - ${bh}px - 13px)`,fontSize:9,color:pos?'var(--green)':'var(--red)',fontWeight:600,whiteSpace:'nowrap',textAlign:'center'}}>
                {pos?'+':''}{Math.round(p)}
              </div>
              <div style={{position:'absolute',top:pos?`calc(50% - ${bh}px)`:'50%',width:'100%',height:bh,background:pos?'rgba(61,190,115,.75)':'rgba(217,79,79,.75)',borderRadius:pos?'2px 2px 0 0':'0 0 2px 2px'}}/>
            </div>
          );
        })}
      </div>
      <div style={{display:'flex',gap:2,marginTop:4}}>
        {last10.map((t,i)=>(
          <div key={i} style={{flex:1,textAlign:'center',fontSize:10,color:'rgba(200,192,168,.65)',fontWeight:600}}>{t.pair ? (t.pair[0]||'')+(t.pair[3]||'') : ''}</div>
        ))}
      </div>
    </div>
  );
}